import styled, { keyframes } from "styled-components";

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const Wrapper = styled.section`
    padding: 100px 5%;
    max-width: var(--maxWidth);
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 56px;
    width: 100%;
    background: linear-gradient(180deg, rgba(10, 10, 10, 0.5) 0%, rgba(15, 15, 15, 0.3) 100%);
    position: relative;

    &::before {
        content: "";
        position: absolute;
        top: -50%;
        right: -20%;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(212, 175, 55, 0.05) 0%, transparent 70%);
        pointer-events: none;
        z-index: 0;
        filter: blur(100px);
    }

    @media (max-width: 768px) {
        padding: 70px 16px;
        gap: 40px;
    }
`;

export const Title = styled.h2`
    font-size: clamp(2.2rem, 4.5vw, 3.2rem);
    color: var(--lightText);
    margin: 0;
    text-align: center;
    position: relative;
    z-index: 1;
    letter-spacing: 0.05em;
    font-weight: 900;
    
    span {
        color: var(--accentGold);
        font-weight: 900;
    }
`;

export const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 32px;
    width: 100%;
    align-items: stretch;
    position: relative;
    z-index: 1;

    @media (max-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 28px;
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 24px;
    }
`;

export const CardImage = styled.div`
    width: 100%;
    height: 240px;
    min-height: 240px;
    flex-shrink: 0;
    padding: 16px 20px;
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.05) 0%, rgba(18, 16, 14, 0.6) 100%);
    border-bottom: 1.5px solid rgba(212, 175, 55, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    cursor: pointer;
    position: relative;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: radial-gradient(circle at center, rgba(212, 175, 55, 0.08) 0%, transparent 70%);
        pointer-events: none;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
        display: block;
        transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        position: relative;
        z-index: 1;
    }

    @media (max-width: 480px) {
        height: 200px;
        min-height: 200px;
        padding: 12px 16px;
    }
`;

export const Card = styled.article`
    background: linear-gradient(135deg, rgba(20, 18, 16, 0.5) 0%, rgba(18, 16, 14, 0.35) 100%);
    border: 1.5px solid rgba(212, 175, 55, 0.2);
    border-radius: 20px;
    overflow: hidden;
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    display: flex;
    flex-direction: column;
    height: 100%;
    box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.2),
        inset 0 1px 0 rgba(212, 175, 55, 0.1),
        0 0 40px rgba(212, 175, 55, 0.05);
    position: relative;
    animation: ${slideInUp} 0.6s ease-out forwards;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.3), transparent);
        z-index: 10;
    }

    @media (hover: hover) {
        &:hover {
            transform: translateY(-12px);
            border-color: rgba(212, 175, 55, 0.4);
            box-shadow: 
                0 24px 56px rgba(0, 0, 0, 0.35),
                inset 0 1px 0 rgba(212, 175, 55, 0.15),
                0 0 60px rgba(212, 175, 55, 0.15);

            ${CardImage} {
                background: linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(18, 16, 14, 0.8) 100%);
                
                img {
                    transform: scale(1.08);
                }
            }
        }
    }
`;

export const ImageModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(10, 10, 10, 0.95);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    padding: 24px;
`;

export const ModalContent = styled.div`
    position: relative;
    width: 100%;
    max-width: 90vw;
    max-height: 90vh;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 15, 15, 0.98);
    border: 1.5px solid rgba(212, 175, 55, 0.2);
    border-radius: 24px;
    box-shadow: 0 40px 100px rgba(0, 0, 0, 0.6), 0 0 60px rgba(212, 175, 55, 0.1);
    backdrop-filter: blur(12px);
    animation: scaleIn 0.3s ease-out;

    @keyframes scaleIn {
        from {
            opacity: 0;
            transform: scale(0.95);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }
`;

export const ModalImage = styled.img`
    max-width: 100%;
    max-height: 90vh;
    border-radius: 16px;
    object-fit: contain;
    width: auto;
    height: auto;
`;

export const ModalClose = styled.button`
    position: absolute;
    top: -16px;
    right: -16px;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 1.5px solid rgba(212, 175, 55, 0.3);
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.1) 100%);
    color: #d4af37;
    font-size: 1.8rem;
    cursor: pointer;
    display: grid;
    place-items: center;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4), 0 0 20px rgba(212, 175, 55, 0.15);
    transition: all 0.3s ease;

    &:hover {
        background: linear-gradient(135deg, rgba(212, 175, 55, 0.3) 0%, rgba(212, 175, 55, 0.2) 100%);
        transform: scale(1.1);
    }
`;

export const CardBody = styled.div`
    padding: 28px 32px 32px;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    position: relative;

    @media (max-width: 480px) {
        padding: 24px 22px 26px;
    }
`;

export const CardFooter = styled.div`
    margin-top: auto;
    padding-top: 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

export const BusinessTitle = styled.h3`
    font-size: 1.35rem;
    color: #faf9f6;
    margin: 0 0 14px 0;
    font-weight: 700;
    letter-spacing: 0.02em;
    line-height: 1.3;
`;

export const BusinessInfo = styled.p`
    font-size: 0.95rem;
    color: #c0c0c0;
    line-height: 1.7;
    flex: 1;
    margin: 0;
    letter-spacing: 0.01em;
`;

export const BusinessContact = styled.p`
    font-size: 0.85rem;
    color: #d4af37;
    margin: 8px 0 0 0;
    line-height: 1.6;
    letter-spacing: 0.02em;
`;

export const BusinessButton = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 12px 24px;
    background: linear-gradient(135deg, #e8c547 0%, #d4af37 50%, #c4941a 100%);
    background-size: 200% 200%;
    color: #1a1410;
    border: none;
    border-radius: 20px;
    font-weight: 700;
    font-size: 0.9rem;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    align-self: flex-start;
    box-shadow: 0 8px 24px rgba(212, 175, 55, 0.2);
    letter-spacing: 0.5px;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent);
        transition: left 0.5s ease;
    }

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 16px 40px rgba(212, 175, 55, 0.3);
        background-position: 200% center;
    }

    &:hover::before {
        left: 100%;
    }

    &:active {
        transform: translateY(-2px);
    }
`;

export const LearnMoreLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 0.9rem;
    color: #d4af37;
    font-weight: 600;
    letter-spacing: 0.3px;
    position: relative;
    text-decoration: none;
    transition: all 0.3s ease;

    &::after {
        content: "↓";
        display: inline-block;
        transition: transform 0.3s ease;
    }

    &:hover {
        color: #e8c547;

        &::after {
            transform: translateY(3px);
        }
    }
`;
